<template>
  <div class="bg-background min-h-screen">
    <!-- Hero Section with improved design -->
    <section class="bg-card border-b border-border py-24">
      <div class="container mx-auto px-4">
        <h1 class="text-5xl font-bold mb-6 text-foreground animate-fade-in">Contact Us</h1>
        <p class="text-xl text-muted-foreground max-w-2xl animate-fade-in-up">We're here to help! Send us a message and we'll get back to you as soon as possible.</p>
      </div>
    </section>

    <!-- Contact Form Section with improved layout -->
    <section class="container mx-auto px-4 py-16 -mt-12 relative z-20">
      <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Contact Information Cards -->
        <div class="lg:col-span-1 space-y-6">
          <!-- Email Card -->
          <div class="bg-card border border-border p-8 rounded-xl shadow-lg transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
            <div class="w-14 h-14 bg-blue-500/15 rounded-full flex items-center justify-center mx-auto mb-6">
              <i class="fas fa-envelope text-blue-600 dark:text-blue-400 text-2xl"></i>
            </div>
            <h3 class="font-semibold text-xl text-foreground mb-3 text-center">Email</h3>
            <p class="text-muted-foreground text-center break-all">support@applypa.com</p>
          </div>

          <!-- Phone Card -->
          <div class="bg-card border border-border p-8 rounded-xl shadow-lg transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
            <div class="w-14 h-14 bg-green-500/15 rounded-full flex items-center justify-center mx-auto mb-6">
              <i class="fas fa-phone text-green-600 dark:text-green-400 text-2xl"></i>
            </div>
            <h3 class="font-semibold text-xl text-foreground mb-3 text-center">Phone</h3>
            <p class="text-muted-foreground text-center">09266266413</p>
          </div>
        </div>

        <!-- Contact Form with improved design -->
        <div class="lg:col-span-2">
          <div class="bg-card border border-border rounded-xl shadow-lg p-8">
            <h2 class="text-2xl font-bold mb-6 text-foreground">Send us a Message</h2>
            <form @submit.prevent="handleSubmit" class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label for="name" class="block text-sm font-medium text-muted-foreground mb-2">Name</label>
                  <div class="relative">
                    <span class="absolute inset-y-0 left-0 pl-3 flex items-center">
                      <i class="fas fa-user text-blue-600 dark:text-blue-400"></i>
                    </span>
                    <input
                      id="name"
                      v-model="form.name"
                      type="text"
                      required
                      class="pl-10 w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      :disabled="loading"
                    />
                  </div>
                </div>

                <div>
                  <label for="email" class="block text-sm font-medium text-muted-foreground mb-2">Email</label>
                  <div class="relative">
                    <span class="absolute inset-y-0 left-0 pl-3 flex items-center">
                      <i class="fas fa-envelope text-blue-600 dark:text-blue-400"></i>
                    </span>
                    <input
                      id="email"
                      v-model="form.email"
                      type="email"
                      required
                      class="pl-10 w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      :disabled="loading"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label for="subject" class="block text-sm font-medium text-muted-foreground mb-2">Subject</label>
                <div class="relative">
                  <span class="absolute inset-y-0 left-0 pl-3 flex items-center">
                    <i class="fas fa-heading text-blue-600 dark:text-blue-400"></i>
                  </span>
                  <input
                    id="subject"
                    v-model="form.subject"
                    type="text"
                    required
                    class="pl-10 w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    :disabled="loading"
                  />
                </div>
              </div>

              <div>
                <label for="message" class="block text-sm font-medium text-muted-foreground mb-2">Message</label>
                <div class="relative">
                  <textarea
                    id="message"
                    v-model="form.message"
                    rows="6"
                    required
                    class="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    :disabled="loading"
                  ></textarea>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  :disabled="loading"
                  class="w-full py-4 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center font-medium text-lg"
                >
                  <span v-if="loading" class="flex items-center">
                    <i class="fas fa-spinner fa-spin mr-2"></i>
                    Sending...
                  </span>
                  <span v-else class="flex items-center">
                    <i class="fas fa-paper-plane mr-2"></i>
                    Send Message
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>

    <!-- Toast Notification -->
    <Toast 
      :show="toast.show" 
      :type="toast.type" 
      :title="toast.title" 
      :message="toast.message" 
      @close="toast.show = false" 
    />
  </div>
</template>

<script>
import { ref, reactive } from 'vue';
import Toast from '@/components/Toast.vue';
import apiClient from '@/services/api';

export default {
  name: 'ContactPage',
  components: {
    Toast
  },
  setup() {
    const loading = ref(false);
    const form = reactive({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    const toast = reactive({
      show: false,
      type: '',
      title: '',
      message: ''
    });

    const handleSubmit = async () => {
      loading.value = true;
      try {
        await apiClient.post('/contact/', form);
        
        // Show success message
        toast.show = true;
        toast.type = 'success';
        toast.title = 'Message Sent';
        toast.message = 'Thank you for your message. We\'ll get back to you soon!';
        
        // Reset form
        form.name = '';
        form.email = '';
        form.subject = '';
        form.message = '';
      } catch (error) {
        console.error('Contact form error:', error);
        // Show error message
        toast.show = true;
        toast.type = 'error';
        toast.title = 'Error';
        toast.message = 'Failed to send message. Please try again later.';
      } finally {
        loading.value = false;
      }
    };

    return {
      form,
      loading,
      toast,
      handleSubmit
    };
  }
};
</script>